<script setup>
import MobileTopBar from "@js/components/TopBar/MobileTopBar.vue";
import Breadcrumbs from "@js/components/Breadcrumbs/Breadcrumbs.vue";
import axios from "axios";
import {reactive, ref, computed} from "vue";
import SpinnerIndicator from "@js/components/Statuses/SpinnerIndicator.vue";
import IconExcelColored from "@js/components/Icons/IconExcelColored.vue";

const props = defineProps({
    priceListData: {type: Array, required: true}
});

const today = new Date().toLocaleDateString('ru-RU', { timeZone: 'Europe/Moscow' });

// локальное состояние
const selected = reactive({
    cats: new Set(),
    subs: new Map(), // catId -> Set(subId)
});

const isCatOn = (id) => selected.cats.has(id);
const toggleCat = (id) => {
    if (isCatOn(id)) {
        selected.cats.delete(id);
        selected.subs.delete(id);
    } else {
        selected.cats.add(id);
        if (!selected.subs.has(id)) selected.subs.set(id, new Set());
    }
};
const isSubOn = (catId, subId) => selected.subs.get(catId)?.has(subId) ?? false;
const toggleSub = (catId, subId) => {
    if (!selected.subs.has(catId)) selected.subs.set(catId, new Set());
    const set = selected.subs.get(catId);
    set.has(subId) ? set.delete(subId) : set.add(subId);
};

// ---- Скачивание XLSX ----
const hasSelection = computed(() => {
    if (selected.cats.size) return true;
    for (const set of selected.subs.values()) if (set.size) return true;
    return false;
});

const buildPayload = () => {
    const categories = Array.from(selected.cats);
    const sub_categories = [];
    selected.subs.forEach((set) => set.forEach((id) => sub_categories.push(id)));
    return {categories, sub_categories};
};

const downloading = ref(false);

const downloadPriceList = async () => {
    if (downloading.value) return;

    downloading.value = true;
    try {
        const res = await axios.post('/axios/price-list', buildPayload(), {responseType: "blob"});

        // Достаём имя файла из Content-Disposition
        let filename = "pricelist.xlsx";
        const cd = res.headers["content-disposition"];
        if (cd) {
            // поддержка filename и filename*
            const m = /filename\*?=(?:UTF-8''|")?([^";]+)"?/i.exec(cd);
            if (m?.[1]) filename = decodeURIComponent(m[1]);
        }

        const blob = new Blob(
            [res.data],
            {
                type:
                    res.headers["content-type"] ||
                    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
            }
        );
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
    } catch (e) {
        console.error(e);
        // тут можно показать тост/ошибку e.response?.data?.message
    } finally {
        downloading.value = false;
    }
};
</script>

<template>
    <MobileTopBar/>
    <div class="container">
        <Breadcrumbs/>
        <h1 class="flex items-center text-2xl font-bold mb-3">
            Прайс лист на металлолома
        </h1>
        <div class="flex max-md:flex-col md:items-center gap-5 mb-5">
            <button @click.prevent="downloadPriceList"
                    :disabled="downloading"
                    class="order-2 md:order-1 max-sm:w-full flex items-center max-sm:justify-center gap-4 rounded-lg bg-[#3ca241] md:hover:bg-[#3ca241]/80 text-white px-5 py-3 disabled:opacity-50 disabled:cursor-not-allowed">
                <IconExcelColored v-if="!downloading" class="h-[30px] w-auto text-white"
                                  first_color="#fff" second_color="#3ca241"/>
                <SpinnerIndicator v-else class="h-[30px] w-auto"/>
                <span class="flex flex-col items-start">
                    <span class="font-medium" v-if="!downloading">
                        Скачать прайс-лист
                    </span>
                    <span v-else>
                        Идет скачивание...
                    </span>
                    <span class="text-xs font-medium">
                        Формат excel (.xlsx)
                    </span>
                </span>
            </button>
            <div class="flex flex-col gap-1 order-1 md:order-2 text-xs font-semibold">
                <div class="flex items-center gap-2">
                    <div class="relative flex size-3">
                        <div
                            class="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-600 opacity-75"></div>
                        <div class="relative inline-flex size-3 rounded-full bg-emerald-600"></div>
                    </div>
                    Цены актуальны на {{today}}
                </div>
                <div>
                    Цены меняются автоматически, при изменениях цен на сайте,
                    <br class="max-md:hidden"/>
                    прайс-лист всегда актуальный.
                </div>
            </div>
        </div>
        <div class="bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden">
            <ul role="list" class="divide-y divide-gray-100">
                <li v-for="cat in priceListData" :key="cat.id" class="px-5 pt-4 pb-3">
                    <div class="flex items-center justify-between gap-4">
                        <div>
                            <div class="text-lg font-medium leading-tight">{{ cat.title }}</div>
                            <div class="text-sm font-medium text-gray-500">Товаров: {{ cat.count }}</div>
                        </div>
                        <!-- Тумблер категории -->
                        <label class="inline-flex items-center cursor-pointer">
                            <input type="checkbox" class="sr-only peer"
                                   :checked="isCatOn(cat.id)" @change="toggleCat(cat.id)">
                            <div
                                class="relative w-11 h-6 bg-gray-200 rounded-full
                       peer-focus:outline-none
                       peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full
                       after:content-[''] after:absolute after:top-[2px] after:start-[2px]
                       after:bg-white after:border-gray-300 after:border after:rounded-full
                       after:h-5 after:w-5 after:transition-all
                       peer-checked:bg-[#3ca241]">
                            </div>
                        </label>
                    </div>

                    <!-- Подкатегории: показываем ТОЛЬКО когда выбрана категория -->
                    <Transition name="collapse">
                        <ul v-if="cat.sub?.length && isCatOn(cat.id)"
                            class="mt-3 sm:mt-4 divide-y divide-gray-100">
                            <li v-for="sub in cat.sub" :key="sub.id" class="pl-4 sm:pl-6 py-3">
                                <div class="flex items-start justify-between gap-4">
                                    <div>
                                        <div class="font-medium leading-tight">{{ sub.title }}</div>
                                        <div class="text-sm font-medium text-gray-500">Товаров: {{ sub.count }}</div>
                                    </div>

                                    <!-- Тумблер подкатегории: отключён пока категория не выбрана -->
                                    <label class="inline-flex items-center cursor-pointer">
                                        <input type="checkbox"
                                               class="sr-only peer"
                                               :checked="isSubOn(cat.id, sub.id)"
                                               :disabled="!isCatOn(cat.id)"
                                               @change="toggleSub(cat.id, sub.id)">
                                        <div
                                            class="relative w-11 h-6 rounded-full
                             bg-gray-200 peer-focus:outline-none
                             peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full
                             after:content-[''] after:absolute after:top-[2px] after:start-[2px]
                             after:bg-white after:border-gray-300 after:border after:rounded-full
                             after:h-5 after:w-5 after:transition-all
                             peer-checked:bg-[#3ca241]
                             disabled:opacity-40 disabled:cursor-not-allowed">
                                        </div>
                                    </label>
                                </div>
                            </li>
                        </ul>
                    </Transition>
                </li>
            </ul>
        </div>
    </div>
</template>

<style scoped>
/* Анимация сворачивания/разворачивания списка подкатегорий */
.collapse-enter-active,
.collapse-leave-active {
    transition: max-height .25s ease, opacity .2s ease;
}

.collapse-enter-from,
.collapse-leave-to {
    max-height: 0;
    opacity: 0;
}

.collapse-enter-to,
.collapse-leave-from {
    max-height: 1000px; /* достаточно большое значение */
    opacity: 1;
}
</style>
